import { Card } from 'primereact/card';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { TabPanel, TabView } from 'primereact/tabview';
import { Toast } from 'primereact/toast';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { ProgressSpinner } from 'primereact/progressspinner';
import DateStringCellTemplate from '../component/date-string-template';
import GenderCellTemplate from '../component/gender-cell-template';
import ParentUpdate from '../component/parent/update';
import ServiceDateTemplate from '../component/service-date-template';
import ServiceIndividualCreate from '../component/service/individual/create';
import ServiceIndividualUpdate from '../component/service/individual/update';
import ServiceGroupCreate from '../component/service/group/create';
import ServiceGroupUpdate from '../component/service/group/update';
import { subscribe, unsubscribe } from '../context/events';
import { useSite } from '../context/site';
import { MSG } from '../shared';

function ParentPage() {
    const { id } = useParams();
    const { ReadOneParent } = useSite();
    const toast = useRef(null);

    const [initializing, setInitializing] = useState(true);
    const [parent, setParent] = useState(null);
    const [openUpdate, setOpenUpdate] = useState(false);
    const [openCreateService, setOpenCreateService] = useState(false);
    const [openUpdateService, setOpenUpdateService] = useState(false);
    const [updateDataService, setUpdateDataService] = useState({});
    const [openCreateServiceGroup, setOpenCreateServiceGroup] = useState(false);
    const [openUpdateServiceGroup, setOpenUpdateServiceGroup] = useState(false);
    const [updateDataServiceGroup, setUpdateDataServiceGroup] = useState({});

    const processEvent = useCallback(
        (event) => {
            const { detail: res, type: action } = event;

            if (res.status === 200) {
                if (
                    action === MSG.create.service.group &&
                    res.data.service.parentsChildren.find((pc) => pc.parent._id === parent._id)
                ) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Group Service Created',
                        detail: 'For current parent',
                        life: 3000
                    });

                    setParent({
                        ...parent,
                        servicesGroup: [...parent.servicesGroup, res.data.service]
                    });
                }

                if (
                    action === MSG.update.service.group &&
                    res.data.service.parentsChildren.find((pc) => pc.parent._id === parent._id)
                ) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Group Service Updated',
                        detail: 'For current parent',
                        life: 3000
                    });

                    setParent({
                        ...parent,
                        servicesGroup: [
                            ...parent.servicesGroup.map((sg) => {
                                if (sg._id === res.data.service._id) {
                                    return res.data.service;
                                }

                                return sg;
                            })
                        ]
                    });
                }

                if (
                    action === MSG.create.service.individual &&
                    res.data.service.parent._id === parent._id
                ) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Individual Service Created',
                        detail: `For ${res.data.service.parent.nameFirst} ${res.data.service.parent.nameLast}`,
                        life: 3000
                    });

                    setParent({
                        ...parent,
                        servicesIndividual: [...parent.servicesIndividual, res.data.service]
                    });
                }

                if (
                    action === MSG.update.service.individual &&
                    res.data.service.parent._id === parent._id
                ) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Individual Service Updated',
                        detail: `For ${res.data.service.parent.nameFirst} ${res.data.service.parent.nameLast}`,
                        life: 3000
                    });

                    setParent({
                        ...parent,
                        servicesIndividual: [
                            ...parent.servicesIndividual.map((si) => {
                                if (si._id === res.data.service._id) {
                                    return res.data.service;
                                }

                                return si;
                            })
                        ]
                    });
                }

                if (action === MSG.update.parent && res.data.parent._id === parent._id) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Parent Updated',
                        detail: `${res.data.parent.nameFirst} ${res.data.parent.nameLast} was updated`,
                        life: 3000
                    });

                    setParent(res.data.parent);
                }
            }
        },
        [parent]
    );

    const TitleDisplay = () => {
        return (
            <span
                className={parent.active ? '' : 'text-red-500'}
                onClick={() => {
                    setOpenUpdate(true);
                }}
            >
                {parent.nameFirst} {parent.nameLast}
                {parent.ccrr ? ' - CCRR' : ''}
                {parent.sponsorJ2TC ? ' - J2TC Sponsor' : ''}
            </span>
        );
    };

    const SubtitleDisplay = () => {
        return (
            <span>
                Added by{' '}
                <span className={parent.addedBy.active ? '' : 'text-red-500'}>
                    {parent.addedBy.name}
                </span>{' '}
                on {new Date(parent.dateAdded).toLocaleDateString()}
            </span>
        );
    };

    const EmailDisplay = () => {
        if (!parent.email) {
            return null;
        }

        return (
            <span>
                <span className="font-semibold">Email</span>
                <br />
                {parent.email}
                <br />
            </span>
        );
    };

    const CellPhoneDisplay = () => {
        if (!parent.cellPhone) {
            return null;
        }

        return (
            <span>
                <span className="font-semibold">Cell Phone</span>
                <br />
                {parent.cellPhone}
                <br />
            </span>
        );
    };

    const HomePhoneDisplay = () => {
        if (!parent.homePhone) {
            return null;
        }

        return (
            <span>
                <span className="font-semibold">Home Phone</span>
                <br />
                {parent.homePhone}
                <br />
            </span>
        );
    };

    const WorkPhoneDisplay = () => {
        if (!parent.workPhone) {
            return null;
        }

        return (
            <span>
                <span className="font-semibold">Work Phone</span>
                <br />
                {parent.workPhone}
                <br />
            </span>
        );
    };

    const AddressDisplay = () => {
        return (
            <span>
                <span className="font-semibold">Address</span>

                <br />

                {!parent.streetAddress1 ? '' : parent.streetAddress1}
                {!parent.streetAddress1 ? '' : <br />}

                {!parent.streetAddress2 ? '' : parent.streetAddress2}
                {!parent.streetAddress2 ? '' : <br />}

                {!parent.city ? '' : parent.city}
                {!parent.city ? '' : ', '}

                {!parent.state.itemData.abbr ? '' : parent.state.itemData.abbr}
                {!parent.state.itemData.abbr ? '' : ' '}

                {!parent.zipCode ? '' : parent.zipCode}

                <br />
            </span>
        );
    };

    const NotesDisplay = () => {
        if (!parent.notes) {
            return null;
        }

        return (
            <span>
                <span className="font-semibold">Notes</span>
                <br />
                <span dangerouslySetInnerHTML={{ __html: parent.notes }}></span>
            </span>
        );
    };

    const SpouseDisplay = () => {
        if (!parent.spouseNameFirst && !parent.spouseNameLast) {
            return null;
        }

        return (
            <span>
                <span className="font-semibold">Spouse</span>
                <br />
                {parent.spouseNameFirst} {parent.spouseNameLast}
                <br />
            </span>
        );
    };

    const GenderDisplay = () => {
        return (
            <span>
                <span className="font-semibold">Gender</span>
                <br />
                {parent.gender.itemName}
                <br />
            </span>
        );
    };

    const RelationshipDisplay = () => {
        return (
            <span>
                <span className="font-semibold">Relationship</span>
                <br />
                {parent.relationship.itemName}
                <br />
            </span>
        );
    };

    const LanguageDisplay = () => {
        return (
            <span>
                <span className="font-semibold">Language</span>
                <br />
                {parent.language.itemName}
                <br />
            </span>
        );
    };

    const NotesTableDisplay = (parent) => {
        if (!parent.notes) {
            return '';
        }

        return <span dangerouslySetInnerHTML={{ __html: parent.notes }}></span>;
    };

    const ActionsTableDisplay = (service) => {
        return (
            <>
                <Button
                    icon="pi pi-user-edit"
                    size="small"
                    rounded
                    severity="help"
                    onClick={() => {
                        setUpdateDataService(service);
                        setOpenUpdateService(true);
                    }}
                />
            </>
        );
    };

    const ParentTableDisplay = () => {
        return `${parent.nameFirst} ${parent.nameLast}`;
    };

    const ChildrenCountDisplay = (service) => {
        return service.childrenPrograms.length;
    };

    const ParentCountDisplayGS = (service) => {
        return service.parentsChildren.length;
    };

    const ChildrenCountDisplayGS = (service) => {
        let count = 0;

        for (const pc of service.parentsChildren) {
            count += pc.children.length;
        }

        return count;
    };

    const EmployeesCountDisplayGS = (service) => {
        return service.employees.length;
    };

    const ActionsTableDisplayGS = (service) => {
        return (
            <>
                <Button
                    icon="pi pi-user-edit"
                    size="small"
                    rounded
                    severity="help"
                    onClick={() => {
                        setUpdateDataServiceGroup(service);
                        setOpenUpdateServiceGroup(true);
                    }}
                />
            </>
        );
    };

    // App Events
    useEffect(() => {
        subscribe(MSG.create.service.individual, processEvent);
        subscribe(MSG.create.service.group, processEvent);

        subscribe(MSG.update.parent, processEvent);
        subscribe(MSG.update.service.individual, processEvent);
        subscribe(MSG.update.service.group, processEvent);

        return () => {
            unsubscribe(MSG.create.service.individual, processEvent);
            unsubscribe(MSG.create.service.group, processEvent);

            unsubscribe(MSG.update.parent, processEvent);
            unsubscribe(MSG.update.service.individual, processEvent);
            unsubscribe(MSG.update.service.group, processEvent);
        };
    }, [processEvent]);

    // Init
    useEffect(() => {
        if (initializing) {
            ReadOneParent({ parentId: id }, (res) => {
                if (res?.data?.parent) {
                    setParent(res.data.parent);
                }

                setInitializing(false);
            });
        }
    }, [id, initializing, ReadOneParent]);

    if (initializing) {
        return (
            <>
                <Card title="Loading parent" className="pageCard">
                    <ProgressSpinner title="Initializing" />
                </Card>
            </>
        );
    }

    if (!parent) {
        return (
            <Card
                title="Parent Dashboard"
                subTitle={`Invalid id: ${id}`}
                className="pageCard"
            ></Card>
        );
    }

    return (
        <Card title={TitleDisplay} subTitle={SubtitleDisplay} className="pageCard">
            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto" style={{ maxWidth: '33vw' }}>
                    <EmailDisplay />
                    <CellPhoneDisplay />
                    <HomePhoneDisplay />
                    <WorkPhoneDisplay />
                    <AddressDisplay />
                </div>

                <div className="flex-auto">
                    <SpouseDisplay />
                    <GenderDisplay />
                    <RelationshipDisplay />
                    <LanguageDisplay />
                </div>

                <div className="flex-auto" style={{ maxWidth: '33vw' }}>
                    <NotesDisplay />
                </div>
            </div>

            <TabView>
                <TabPanel header={`Children: ${parent.children.length}`}>
                    <DataTable
                        value={parent.children}
                        rowClassName={(child) => (child.active ? 'g' : 'r')}
                    >
                        <Column field="nameFirst" header="First Name"></Column>

                        <Column field="nameLast" header="Last Name"></Column>

                        <Column
                            field="gender"
                            header="Gender"
                            body={(data) => {
                                return <GenderCellTemplate child={data} />;
                            }}
                        ></Column>

                        <Column
                            field="dob"
                            header="Date of Birth"
                            body={(data) => {
                                return <DateStringCellTemplate child={data} />;
                            }}
                        ></Column>

                        <Column header="Notes" body={NotesTableDisplay}></Column>
                    </DataTable>
                </TabPanel>

                <TabPanel header={`Group Services: ${parent.servicesGroup.length}`}>
                    <Button
                        icon="pi pi-plus"
                        rounded
                        severity="success"
                        onClick={() => {
                            setOpenCreateServiceGroup(true);
                        }}
                    />

                    <Divider />

                    <DataTable value={parent.servicesGroup}>
                        <Column
                            field="dateService"
                            body={ServiceDateTemplate}
                            header="Service Date"
                        ></Column>
                        <Column field="program.programName" header="Program Location"></Column>
                        <Column field="serviceType.itemName" header="Service Type"></Column>
                        <Column field="activityName" header="Activity"></Column>
                        <Column field="unknownParents" header="Unknown Parents"></Column>
                        <Column field="unknownChildren" header="Unknown Children"></Column>
                        <Column body={EmployeesCountDisplayGS} header="Employees"></Column>
                        <Column body={ParentCountDisplayGS} header="Parents"></Column>
                        <Column body={ChildrenCountDisplayGS} header="Children"></Column>
                        <Column body={NotesTableDisplay} header="Notes"></Column>
                        <Column
                            header="Actions"
                            bodyStyle={{ width: '150px' }}
                            body={ActionsTableDisplayGS}
                        ></Column>
                    </DataTable>

                    {!openCreateServiceGroup ? null : (
                        <ServiceGroupCreate
                            open={openCreateServiceGroup}
                            data={parent}
                            onClose={() => {
                                setOpenCreateServiceGroup(false);
                            }}
                        />
                    )}

                    {!openUpdateServiceGroup ? null : (
                        <ServiceGroupUpdate
                            open={openUpdateServiceGroup}
                            data={updateDataServiceGroup}
                            onClose={() => {
                                setUpdateDataServiceGroup({});
                                setOpenUpdateServiceGroup(false);
                            }}
                        />
                    )}
                </TabPanel>

                <TabPanel header={`Individual Services: ${parent.servicesIndividual.length}`}>
                    <Button
                        icon="pi pi-plus"
                        rounded
                        severity="success"
                        onClick={() => {
                            setOpenCreateService(true);
                        }}
                    />

                    <Divider />

                    <DataTable value={parent.servicesIndividual}>
                        <Column
                            field="dateService"
                            body={ServiceDateTemplate}
                            header="Service Date"
                        ></Column>
                        <Column field="employee.name" header="Employee"></Column>
                        <Column body={ParentTableDisplay} header="Parent"></Column>
                        <Column field="serviceType.itemName" header="Service Type"></Column>
                        <Column body={ChildrenCountDisplay} header="Children Count"></Column>
                        <Column body={NotesTableDisplay} header="Notes"></Column>
                        <Column
                            header="Actions"
                            bodyStyle={{ width: '150px' }}
                            body={ActionsTableDisplay}
                        ></Column>
                    </DataTable>

                    {!openCreateService ? null : (
                        <ServiceIndividualCreate
                            open={openCreateService}
                            data={parent}
                            onClose={() => {
                                setOpenCreateService(false);
                            }}
                        />
                    )}

                    {!openUpdateService ? null : (
                        <ServiceIndividualUpdate
                            open={openUpdateService}
                            data={updateDataService}
                            onClose={() => {
                                setUpdateDataService({});
                                setOpenUpdateService(false);
                            }}
                        />
                    )}
                </TabPanel>
            </TabView>

            {!openUpdate ? null : (
                <ParentUpdate
                    open={openUpdate}
                    data={parent}
                    onClose={() => {
                        setOpenUpdate(false);
                    }}
                />
            )}

            <Toast ref={toast} />
        </Card>
    );
}

export default ParentPage;
